import LinkIcon from '@atlaskit/icon/glyph/link';
import { useCallbackEffect, useLoggingCallback } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { Hyperlink } from '@databases/types/database';
import { loadHyperlink } from '@databases/utils/redux/hyperlinks/actions';
import { selectHyperlinkOptional } from '@databases/utils/redux/hyperlinks/selectors';
import { getFallbackHyperlinkLabel, isValidHyperlinkUrl } from '@databases/utils/utils';
import React, { FunctionComponent } from 'react';

import AbstractLink from './AbstractLink';

export interface HyperlinkLinkProps {
  hyperlink: Hyperlink;
  noFocus?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  noResolve?: boolean;
  useSmartLink?: boolean;
}

/**
 * Renders a formatted placeholder representing a page reference. The page reference is resolved
 * automatically to retrieve the page URL. Until the page reference is resolved, the page title is
 * shown as plain text, once it is resolved, it is converted into a link to the page.
 */
const HyperlinkLink: FunctionComponent<HyperlinkLinkProps> = ({
  hyperlink,
  noFocus,
  onClick,
  onFocus,
  noResolve,
  useSmartLink,
}) => {
  const resolvedHyperlink = defaultStoreUtils.useSelector((state) =>
    selectHyperlinkOptional(state, { url: hyperlink.url })
  );

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useLoggingCallback(async () => {
      if (
        !noResolve &&
        (!hyperlink.isLabelExplicit || !hyperlink.label) &&
        isValidHyperlinkUrl(hyperlink.url)
      ) {
        await dispatch(loadHyperlink(hyperlink.url));
      }
    }, [dispatch, hyperlink.isLabelExplicit, hyperlink.label, hyperlink.url, noResolve])
  );

  let label;
  if (hyperlink.isLabelExplicit && hyperlink.label) {
    label = hyperlink.label;
  } else if (noResolve) {
    label = hyperlink.url;
  } else {
    label =
      (resolvedHyperlink ? resolvedHyperlink.label : hyperlink.label) ||
      getFallbackHyperlinkLabel(hyperlink.url);
  }

  return (
    <AbstractLink
      href={
        hyperlink.url && isValidHyperlinkUrl(hyperlink.url) ? hyperlink.url : undefined
      }
      onFocus={onFocus}
      onClick={onClick}
      noFocus={noFocus}
      iconBefore={<LinkIcon size="small" label="" />}
      noResolve={noResolve}
      useSmartLink={useSmartLink}
    >
      {label}
    </AbstractLink>
  );
};

export default HyperlinkLink;
